import {
  Box,
  Button,
  Divider,
  IconButton,
  Popover,
  SxProps,
} from '@mui/material'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { activateTool } from '../../actions/permit'
import { RootState } from '../../store'
import { SvgIconProps } from '../Svgs'

interface ToolSelectProps {
  items: {
    name: string
    tools: {
      name: string
      icon: React.FC<SvgIconProps>
    }[]
  }
  onSelect: (groupName: string) => void
  sx: SxProps
  disabled?: boolean
}

const ToolSelect: React.FC<ToolSelectProps> = ({
  items,
  onSelect,
  sx,
  disabled,
}) => {
  const dispatch = useDispatch<any>()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isSelected, setIsSelected] = useState<boolean>(false)
  const [hoveredTool, setHoveredTool] = useState<string | null>(null)
  const permit = useSelector((state: RootState) => state.permit)
  const open = Boolean(anchorEl)
  const id = open ? 'tools-list' : undefined

  const handleArrowClick = (event: any) => {
    setIsSelected(true)
    setAnchorEl(event.currentTarget)
  }

  const handleToolClick = (name: string) => {
    if (name.length) {
      dispatch(activateTool(name))
      onSelect(items.name)
    }
  }

  const handleClose = (name: string) => {
    setIsSelected(false)
    if (name.length) {
      dispatch(activateTool(name))
      onSelect(items.name)
    }

    setAnchorEl(null)
  }

  const getActiveTool = () => {
    const activeTool = items.tools.find(
      (item) => item.name === permit.activeTool,
    )

    return activeTool ?? items.tools[0]
  }

  return (
    <>
      <Button size='small' disabled={disabled} sx={sx}>
        <Box
          columnGap='5px'
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '100%',
            width: '100%',
          }}
        >
          <Box
            onClick={() => handleToolClick(items.tools[0].name)}
            sx={{
              width: '80%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {getActiveTool()!.icon({ color: (sx as any)?.color })}
          </Box>
          <Box
            onClick={handleArrowClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              borderRadius: '4px',
              background: isSelected ? '#1705E5' : 'transparent',
              px: '3px',
            }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='8'
              height='5'
              viewBox='0 0 8 5'
              fill='none'
            >
              <path
                d='M7.13672 4L4.06865 0.931934L1.00059 4'
                stroke={isSelected ? 'white' : (sx as any)?.color}
                strokeMiterlimit='10'
              />
            </svg>
          </Box>
        </Box>
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{
          style: {
            width: anchorEl ? anchorEl.parentElement?.clientWidth : 'auto',
            borderRadius: '4px',
          },
        }}
      >
        <Box
          display='flex'
          flexDirection='column'
          sx={{
            background: '#F2F2F2',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {items.tools
            .filter((item) => item.name !== getActiveTool().name)
            .reverse()
            .map((item, index) => (
              <Box
                key={index}
                onMouseEnter={() => setHoveredTool(item.name)}
                onMouseLeave={() => setHoveredTool(null)}
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  '&:hover': {
                    alignItems: 'center',
                    background: '#1705E5',
                  },
                }}
              >
                <IconButton
                  onClick={() => handleClose(item.name)}
                  sx={{
                    width: '42px',
                    height: '38px',
                  }}
                >
                  <item.icon
                    color={hoveredTool === item.name ? 'white' : 'black'}
                  />
                </IconButton>
                {index !== items.tools.length - 2 && (
                  <Divider
                    flexItem
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 'calc((52px - 70%) / 2)',
                      width: '70%',
                      backgroundColor: 'rgba(119, 119, 119, 0.60)',
                    }}
                  />
                )}
              </Box>
            ))}
        </Box>
      </Popover>
    </>
  )
}

export default ToolSelect
